import { FC, useEffect, useState } from "react";
import { Form } from "../../../../components/common/Forms";
import { managerInfoFields } from "../../../../config/viewDocumentsFields";
import "form-pattern/dist/main.css";
import lockIcon from "/assets/images/lockSolid.svg";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  fetchManagerInformation,
  updateManagerDetails,
  getManagerByNUID,
} from "../../../../store/slices/Citizenship/viewDocumentSlice";
import Notifications from "../../../../components/common/Notifications";
import ScrollToTopBtn from "../../../../components/common/ScrollToTopBtn";
import UploadErrorBanner from "./UploadErrorBanner";
import { trackButtonClick, trackSearch, trackFormSave, trackFormError } from '../../../../services/analytics';

const ManagerInfo: FC = () => {
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { id } = useParams();
  const dispatch = useAppDispatch();
  const {
    managerInformation,
    managerInformationError,
    managerSearchResult,
    managerSearchLoading,
    managerSearchError,
    managerUpdateError,
  } = useAppSelector((state) => state.viewDocument);
  const [searchNUID, setSearchNUID] = useState<string>("");
  const [showSearchResult, setShowSearchResult] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [showLocalSearchError, setShowLocalSearchError] =
    useState<boolean>(false);
  const [searchValidationError, setSearchValidationError] = useState<string>("");

  useEffect(() => {
    if (id) {
      dispatch(fetchManagerInformation(id));
    }
  }, [id]);

  // clear any previous manager search state when entering this view
  useEffect(() => {
    dispatch({ type: "viewDocument/clearManagerSearch" });
    setSearchNUID("");
    setHasSearched(false);
    setShowSearchResult(false);
    setShowLocalSearchError(false);
  }, []);

  useEffect(() => {
    const hasResult =
      managerSearchResult &&
      Object.keys(managerSearchResult).length > 0 &&
      !managerSearchError;
    setShowSearchResult(Boolean(hasResult && hasSearched));
  }, [managerSearchResult, managerSearchError, hasSearched]);

  // auto-hide search error and clear input after 10 seconds
  useEffect(() => {
    if (!managerSearchError) {
      setShowLocalSearchError(false);
      return;
    }

    setShowLocalSearchError(true);
    const t = setTimeout(() => {
      setShowLocalSearchError(false);
      setSearchNUID("");
    }, 5000);

    return () => clearTimeout(t);
  }, [managerSearchError]);

  // Handle update manager details error
  useEffect(() => {
    if (managerUpdateError) {
      setErrorMessage(managerUpdateError);
      setShowErrorToast(true);
      const t = setTimeout(() => {
        setShowErrorToast(false);
        setErrorMessage("");
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [managerUpdateError]);

  if (managerInformationError) {
    return (
      <div className="margin-top-4u flex items-center justify-center min-h-[65vh]">
        <div className="w-full max-w-2xl">
          <div className="flex flex-col items-center text-center gap-4">
            <Notifications messageInfo={managerInformationError} status={false} />
          </div>
        </div>
      </div>
    );
  }

  const handleSearchManager = () => {
    if (!searchNUID || !searchNUID.trim()) {
      setSearchValidationError("Please enter the manager NUID.");
      setShowSearchResult(false);
      setHasSearched(false);
      trackFormError('citizenship', ['managerNUID']);
      return;
    }
    
    // Clear validation error
    setSearchValidationError("");
    
    if (searchNUID.trim()) {
      console.log(`📊 Analytics: Search manager by NUID - ${searchNUID}`);
      trackButtonClick('Search Manager', 'manager-info', { searchNUID });
      trackSearch('manager-nuid', searchNUID, 0);
      setHasSearched(true);
      dispatch(getManagerByNUID(searchNUID));
    }
  };

  const handleSave = async () => {
    if (!searchNUID) {
      console.error("Manager ID is missing");
      return;
    }

    // prevent saving if there was an error during manager search
    if (managerSearchError) {
      console.error("Cannot save: manager search returned an error");
      return;
    }
    console.log(`📊 Analytics: Save manager details - NUID: ${searchNUID}`);
    trackButtonClick('Save', 'manager-info', { managerNUID: searchNUID, applicationId: id });

    try {
      // call patch API and capture result
      const res: any = await dispatch(
        updateManagerDetails({
          altMgrNuid: searchNUID,
        })
      );

      // if update succeeded, and we have a recent search result, clear input and redux search
      if (res?.type?.endsWith("/fulfilled")) {
        if (
          managerSearchResult &&
          Object.keys(managerSearchResult).length > 0
        ) {
          setSearchNUID("");
          setHasSearched(false);
          // clear redux search result to avoid it showing elsewhere
          dispatch({ type: "viewDocument/clearManagerSearch" });
        }

        // re-fetch manager details for this application
        if (id) {
          await dispatch(fetchManagerInformation(id));
        }

        // hide the green search result box
        setShowSearchResult(false);

        // Show success toast
        setShowSuccessToast(true);
        console.log(`📊 Analytics: Manager info saved successfully`);
        trackFormSave('citizenship', undefined, { managerNUID: searchNUID, applicationId: id });
        setTimeout(() => setShowSuccessToast(false), 25000);
      } else if (res?.type?.endsWith("/rejected")) {
        // Error is handled by useEffect watching managerUpdateError
        trackFormError('citizenship', ['managerDetails']);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "An unexpected error occurred";
      setErrorMessage(errorMsg);
      setShowErrorToast(true);
      console.error("Failed to save manager details:", err);
      trackFormError('citizenship', ['managerDetails']);
      setTimeout(() => setShowErrorToast(false), 5000);
    }
  };
  return (
    <div className="margin-top-4u">
      {showSuccessToast && (
        <Notifications
          messageInfo="Manager information has been saved successfully."
          status={true}
        />
      )}
      {showErrorToast && errorMessage && (
        <Notifications
          messageInfo={errorMessage}
          status={false}
        />
      )}
      <h3 className="h3">Manager Information</h3>
      {searchValidationError && (
        <UploadErrorBanner
          header="Manager NUID required"
          message={searchValidationError}
          className="!mt-0"
        />
      )}
      <Form
        fields={managerInfoFields}
        buttons={[]}
        initialValues={managerInformation || {}}
        managerInfo={true}
      />
      <div className="margin-top-[32px] ">
        <div className="flex gap-4">
          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <label htmlFor="self-reported-manager">Self-reported manager</label>
            <div className="w-full relative ">
              <input
                autoComplete="on"
                className="w-full min-w-0 block ds-form__disabled-field bg-gray-100 text-gray-600 pr-10"
                id="self-reported-manager"
                name="self-reported-manager"
                type="text"
                value={managerInformation?.altManagerName || ""}
                placeholder=""
                aria-describedby="self-reported-manager-helper"
                disabled
                style={{ width: "100%" }}
              />
              <img
                src={lockIcon}
                alt=""
                aria-hidden="true"
                className="absolute right-[12px] top-[12px]"
              />
              <span
                id="self-reported-manager-helper"
                className="ds-form__text-input-helper"
              >
                Manager assigned via HR connect
              </span>
            </div>
          </div>

          <div
            className="ds-form w-full"
            data-ds-theme="vessel"
            data-ds-variant="input"
            data-ds-version="1"
          >
            <label htmlFor="text-input-1">NUID</label>

            <div className="flex items-start gap-4 mt-2 flex-wrap">
              <div className="w-[70%] flex flex-col">
                <input
                  autoComplete="on"
                  className="w-full"
                  id="text-input-1"
                  type="text"
                  value={searchNUID}
                  onChange={(e) => {
                    setSearchNUID(e.target.value);
                    // Clear validation error when user starts typing
                    if (searchValidationError) {
                      setSearchValidationError("");
                    }
                  }}
                  aria-describedby="helpertext"
                  placeholder="Enter manager NUID"
                  style={{ width: "100%" }}
                />
                <span
                  id="helpertext"
                  className="ds-form__text-input-helper"
                  style={{ width: "100%" }}
                >
                  Manager NUID manually entered by employee
                </span>
              </div>
              <button
                className="button -primary whitespace-nowrap"
                data-analytics-type="button"
                data-analytics-click="Default"
                onClick={handleSearchManager}
                disabled={managerSearchLoading}
              >
                {managerSearchLoading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        </div>
       
        {showLocalSearchError && managerSearchError && (
          <div className="margin-top-2u p-4 bg-red-50 border border-red-200 rounded">
            <div className="flex items-center gap-2">
              <p className="text-red-700 font-medium">{managerSearchError}</p>
            </div>
          </div>
        )}
        {showSearchResult && (
          <div className="margin-top-2u p-4 bg-green-50 border border-green-200 rounded">
            <p className="text-green-800 font-semibold mb-3">
              Manager found:
              {managerSearchResult?.altMgrName}
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 !mt-8">
          <button
            className="button -primary -light"
            data-analytics-type="button"
            data-analytics-click="Default"
            onClick={handleSave}
            disabled={managerSearchLoading}
          >
            Save
          </button>
        </div>
      </div>
      <ScrollToTopBtn />
    </div>
  );
};

export default ManagerInfo;
